import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  LayoutDashboard,
  CalendarRange,
  BookOpen,
  Users,
  Sparkles,
  DollarSign,
  Settings,
  Moon,
  Radio,
  AlertTriangle,
  Wrench,
  Package,
  BarChart2,
  MessageSquare,
  Bell,
  ClipboardCheck,
  Shield,
  KeyRound,
  WalletCards,
  History,
  LogIn,
  LogOut,
  BedDouble,
  type LucideIcon,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
} from '@/components/ui/command';
import { useGuestsQuery } from '@/hooks/api/use-guests-query';
import { usePermissions } from '@/components/auth/PermissionGate';
import { useAuthStore } from '@/store/auth.store';
import { isSystemOwner } from '@/lib/authz';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CommandPage {
  label: string;
  path: string;
  icon: LucideIcon;
  shortcut?: string;
  permission?: string | string[];
  masterOnly?: boolean;
  hideForMaster?: boolean;
}

const pages: CommandPage[] = [
  { label: 'Visão geral', path: '/visao-geral', icon: LayoutDashboard, shortcut: '' },
  { label: 'Ocupação', path: '/ocupacao', icon: CalendarRange, shortcut: '' },
  { label: 'Reservas', path: '/reservas', icon: BookOpen, shortcut: '' },
  { label: 'Hóspedes', path: '/hospedes', icon: Users, shortcut: '' },
  { label: 'Portal dos hóspedes', path: '/portais-dos-hospedes', icon: ClipboardCheck, shortcut: '', permission: 'bookings.view', hideForMaster: true },
  { label: 'Governança', path: '/governanca', icon: Sparkles, shortcut: '' },
  { label: 'Financeiro', path: '/financeiro', icon: DollarSign, shortcut: '', permission: 'financial.view' },
  { label: 'Auditoria noturna', path: '/auditoria-noturna', icon: Moon, shortcut: '', permission: 'financial.view' },
  { label: 'Channel Manager', path: '/gerenciador-de-canais', icon: Radio, shortcut: '' },
  { label: 'Alertas operacionais', path: '/alertas-operacionais', icon: AlertTriangle, shortcut: '' },
  { label: 'Manutenções', path: '/manutencoes', icon: Wrench, shortcut: '' },
  { label: 'Estoque e consumo', path: '/estoque-e-consumo', icon: Package, shortcut: '' },
  { label: 'Relatórios', path: '/relatorios', icon: BarChart2, shortcut: '', permission: 'reports.view' },
  { label: 'Mensagens da equipe', path: '/mensagens', icon: MessageSquare, shortcut: '' },
  { label: 'Central de avisos', path: '/notificacoes', icon: Bell, shortcut: '' },
  { label: 'Configurações', path: '/configuracoes', icon: Settings, shortcut: '', permission: 'settings.edit' },
  { label: 'Equipe', path: '/configuracoes/equipe', icon: Users, shortcut: '', permission: 'settings.edit' },
  { label: 'Carteira', path: '/configuracoes/carteira', icon: WalletCards, shortcut: '', permission: 'financial.view' },
  { label: 'Chaves de API', path: '/configuracoes/chaves-de-api', icon: KeyRound, shortcut: '', permission: 'settings.edit' },
  { label: 'Histórico de Cobrança', path: '/financeiro/historico-de-cobrancas', icon: History, shortcut: '', permission: 'financial.view' },
  { label: 'Central Allmight', path: '/administracao/telemetria', icon: Shield, shortcut: '', masterOnly: true },
];

const quickActions = [
  { label: 'Novo check-in', icon: LogIn, shortcut: '⌘I', path: '/reservas?status=confirmed' },
  { label: 'Novo check-out', icon: LogOut, shortcut: '⌘O', path: '/reservas?status=checkedin' },
  { label: 'Nova reserva', icon: BedDouble, shortcut: '⌘N', path: '/reservas?new=true' },
];

// Os resultados da busca de hóspedes vêm da API

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const user = useAuthStore((state) => state.user);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visiblePages = pages.filter((page) =>
    (!page.permission || can(page.permission))
      && (!page.masterOnly || isSystemOwner(user))
      && (!page.hideForMaster || !isSystemOwner(user)),
  );
  const { data: guestsData } = useGuestsQuery(open && debouncedQuery.length > 1 ? { search: debouncedQuery } : undefined);
  const guests = guestsData ?? [];

  // Aguarda 300 ms entre buscas para não chamar a API a cada tecla
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(value), 300);
  }, []);

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const runCommand = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Buscar hóspede, quarto, reserva ou página..."
        value={query}
        onValueChange={handleQueryChange}
      />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>

        <CommandGroup heading="Páginas">
          {visiblePages.map((page) => {
            const Icon = page.icon;
            return (
              <CommandItem
                key={page.path}
                value={page.label}
                onSelect={() => runCommand(() => navigate(page.path))}
              >
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{page.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ações Rápidas">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <CommandItem
                key={action.label}
                value={action.label}
                onSelect={() => runCommand(() => navigate(action.path))}
              >
                <Icon className="mr-2 h-4 w-4 text-primary" />
                <span>{action.label}</span>
                {action.shortcut && (
                  <CommandShortcut>{action.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {debouncedQuery.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Hóspedes">
              {guests.map((guest) => (
                  <CommandItem
                    key={guest.id}
                    value={`${guest.first_name ?? ''} ${guest.last_name ?? ''}`.trim()}
                    onSelect={() => runCommand(() => navigate(`/hospedes?id=${guest.id}`))}
                  >
                    <Users className="mr-2 h-4 w-4 text-sky-400" />
                    <span>{guest.first_name} {guest.last_name}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
